import React from 'react';
import { AlertCircle, X } from 'lucide-react';

interface OrderConfirmationDialogProps {
  isOpen: boolean;
  totalCost: number;
  totalHours: number;
  discountActive: boolean;
  discountValue: number;
  modules: Array<{ name: string; hours: number }>;
  onConfirm: () => void;
  onCancel: () => void;
}

const OrderConfirmationDialog: React.FC<OrderConfirmationDialogProps> = ({
  isOpen,
  totalCost,
  totalHours,
  discountActive,
  discountValue,
  modules,
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        {/* Header */}
        <div className="border-b border-gray-200 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 p-2 rounded-lg">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              Bestellung bestätigen
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            <strong>Hey, kurz checken:</strong> Bist du dir sicher, dass deine Konfiguration so passt?
          </p>
          <p className="text-sm text-gray-600 mb-6">
            Nach dem Absenden gilt das Projekt als <strong className="text-orange-600">beauftragt</strong> und
            wir starten direkt mit der Umsetzung. Falls du noch was ändern willst, jetzt ist der Moment! 😊
          </p>

          {/* Quick Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm font-semibold text-gray-700 mb-3">Deine Auswahl:</p>
            <div className="space-y-1 mb-3">
              {modules.map((module, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-gray-600">• {module.name}</span>
                  <span className="text-gray-500">{module.hours}h</span>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-200 pt-3 mt-3">
              {discountActive && (
                <div className="flex justify-between text-sm text-green-600 mb-1">
                  <span>Rabatt ({discountValue}%):</span>
                  <span className="font-medium">Aktiv ✓</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Gesamtpreis:</span>
                <span className="text-xl font-bold text-blue-600">
                  €{totalCost.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>Stunden:</span>
                <span>{totalHours}h</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
            >
              Nochmal checken
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Ja, beauftragen! 🚀
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationDialog;

